import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Waves } from "lucide-react";

/**
 * Session Volatility Heatmap strip.
 *
 * Shows every symbol as a color-coded chip sorted hottest→calmest, so a trader
 * can eyeball "where the action is right now" without scrolling the watchlist.
 * Clicking a chip selects that symbol in the Terminal.
 *
 * Buckets:
 *   calm      → gray
 *   normal    → sky
 *   elevated  → amber
 *   hot       → red
 */
const BUCKET_STYLES = {
    calm:     { chip: "bg-muted/60 text-muted-foreground border-border",         label: "Calm" },
    normal:   { chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",       label: "Normal" },
    elevated: { chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40", label: "Elevated" },
    hot:      { chip: "bg-danger/15 text-danger border-danger/40 font-black",     label: "Hot" },
};

export default function VolatilityHeatmap({ assetClass = "all", onSelect, selected }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/market/volatility", { params: { asset_class: assetClass } });
            setItems(data.items || []);
            setError(false);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        const id = setInterval(load, 30_000); // refresh every 30s
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetClass]);

    if (loading && items.length === 0) {
        // Deliberately render nothing during the initial volatility fetch —
        // previously we showed a shimmering grey skeleton row here, but users
        // reported it as a "grey bar that floats around the tabs then
        // disappears" every time the terminal (re)mounted or switched asset
        // classes. Silent-until-ready is calmer and avoids the transient
        // flash entirely.
        return null;
    }

    if (error || items.length === 0) return null;

    // Top-level "hottest" pair for the header caption.
    const hottest = items[0];
    const hottestStyle = BUCKET_STYLES[hottest?.heat_bucket] || BUCKET_STYLES.normal;

    return (
        <div className="mb-4 border border-border bg-card rounded-lg p-3" data-testid="volatility-heatmap">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-danger" />
                    <div className="overline">Session volatility · sorted hottest first</div>
                </div>
                {hottest && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        Look here now:
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold border ${hottestStyle.chip}`}
                        >
                            {hottest.symbol} · {hottest.heat}×
                        </span>
                    </div>
                )}
            </div>
            <div
                className="flex gap-1.5 overflow-x-auto pb-1 -mb-1"
                data-testid="volatility-heatmap-chips"
                style={{ scrollbarWidth: "thin" }}
            >
                {items.map((it) => {
                    const style = BUCKET_STYLES[it.heat_bucket] || BUCKET_STYLES.calm;
                    const isSelected = selected === it.symbol;
                    return (
                        <button
                            key={it.symbol}
                            onClick={() => onSelect?.(it.symbol)}
                            data-testid={`heat-chip-${it.symbol}`}
                            title={`${it.symbol} · heat ${it.heat}× (${style.label})  ·  current 5-tick range: ${it.current_range_pct}%  ·  baseline: ${it.baseline_range_pct}%`}
                            className={`shrink-0 border rounded-md px-2.5 py-1.5 text-xs font-mono font-semibold transition-all hover:scale-105 ${style.chip} ${
                                isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-card" : ""
                            }`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className="font-bold">{it.symbol}</span>
                                {it.asset_class === "fx" && (
                                    <span className="text-[8px] uppercase px-1 rounded bg-primary/20 text-primary">FX</span>
                                )}
                            </div>
                            <div className="text-[10px] mt-0.5 opacity-80">{it.heat > 0 ? `${it.heat}×` : "—"}</div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
